import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { Combobox } from '@aeris/ui'

import {
  createProductCategory,
  fetchProductCategories,
} from '../lib/api/product-categories.ts'
import { ApiError } from '../lib/api.ts'
import { categoryLabel } from '../lib/category-label.ts'

/**
 * A categoria do produto, escolhida em vez de digitada — F163.
 *
 * O mesmo desenho do campo de marca (`brand-field.tsx`), pelo mesmo motivo: a
 * categoria nova aparece no meio do cadastro de um produto, e mandar a pessoa
 * a outra tela criar a linha é o caminho que faz alguém desistir e deixar o
 * campo vazio.
 *
 * A diferença em relação à marca está no valor: aqui a escolha é o `id` da
 * categoria, e não o nome — o produto aponta por chave estrangeira (migração
 * 0092). Renomear a categoria depois não desgarra produto nenhum.
 */

/** Um sentinela, como no campo de marca: a linha "criar" é uma opção como as outras. */
const CREATE = ' criar'

export type CategoryFieldProps = {
  /** O id da categoria. Nulo é "sem categoria". */
  value: string | null
  onValueChange: (id: string | null) => void
  id?: string
  invalid?: boolean
  'aria-describedby'?: string | undefined
}

export const CategoryField = ({
  value,
  onValueChange,
  id,
  invalid = false,
  ...aria
}: CategoryFieldProps): ReactNode => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  const categories = useQuery({
    queryKey: ['product-categories', 'active'],
    queryFn: async ({ signal }) =>
      await fetchProductCategories('active', signal),
  })

  const create = useMutation({
    mutationFn: async (name: string) => await createProductCategory({ name }),
    onSuccess: async (category) => {
      setError(null)
      onValueChange(category.id)
      await queryClient.invalidateQueries({ queryKey: ['product-categories'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível criar a categoria.'
      )
    },
  })

  const term = search.trim().toLowerCase()
  const all = categories.data ?? []
  // "cabos" acha a de cima e as filhas dela (F243).
  const matches = all.filter((category) =>
    categoryLabel(category).toLowerCase().includes(term)
  )
  // Criar pelo campo cria uma de cima; a subcategoria vem pela planilha.
  const exact = all.some(
    (category) =>
      category.parentId === null && category.name.trim().toLowerCase() === term
  )
  const offerCreate = term.length > 0 && !exact

  const items = [
    ...(offerCreate
      ? [{ value: CREATE, label: `Criar a categoria “${search.trim()}”` }]
      : []),
    ...matches.map((category) => ({
      value: category.id,
      label: categoryLabel(category),
      hint:
        category.usageCount === 0
          ? undefined
          : `${String(category.usageCount)} produto${category.usageCount === 1 ? '' : 's'}`,
    })),
  ]

  const chosen = all.find((category) => category.id === value)

  return (
    <div className="flex flex-col gap-1">
      <Combobox
        {...(id === undefined ? {} : { id })}
        value={value}
        selectedLabel={chosen === undefined ? null : categoryLabel(chosen)}
        onValueChange={(next) => {
          if (next === CREATE) {
            create.mutate(search.trim())
            return
          }
          onValueChange(next)
        }}
        items={items}
        search={search}
        onSearchChange={setSearch}
        loading={categories.isPending || create.isPending}
        invalid={invalid}
        clearable
        placeholder="Bateria"
        emptyMessage={
          categories.isError
            ? 'Não foi possível carregar as categorias.'
            : 'Nenhuma categoria cadastrada ainda. Digite para criar a primeira.'
        }
        {...aria}
      />
      {error === null ? null : (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {error}
        </p>
      )}
    </div>
  )
}
